import { useRef, useState } from 'react';
import { cargoApi, type ShipmentDetail } from '@/api/cargo';
import { PageHead, SectionCard, StatusBadge } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { SCAN_ACTIONS, SHIPMENT_STATUS_COLOR } from '@/shared/cargo';
import { dt } from '@/shared/format';

interface ScanLog {
  trackingNo: string;
  statusLabel: string;
  status: number;
  at: string;
}

export function ScanPage() {
  const toast = useToast();
  const [status, setStatus] = useState<number>(1);
  const [code, setCode] = useState('');
  const [last, setLast] = useState<ShipmentDetail | null>(null);
  const [log, setLog] = useState<ScanLog[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function scan() {
    const tn = code.trim();
    if (!tn) return;
    setBusy(true);
    try {
      const res = await cargoApi.scan(tn, { Status: status });
      setLast(res);
      setLog((l) => [{ trackingNo: res.TrackingNo, statusLabel: res.StatusLabel, status: res.Status, at: new Date().toISOString() }, ...l].slice(0, 20));
      toast(`${res.TrackingNo} → ${res.StatusLabel}`, 'success');
      setCode('');
      inputRef.current?.focus();
    } catch (e) {
      toast((e as Error).message, 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHead title="Scan станц" subtitle="Track дугаар уншуулж олноор нь төлөв шинэчилнэ." />

      <div className="ts-row">
        <div style={{ gridColumn: 'span 7' }}>
          <SectionCard icon="bi-upc-scan" title="Үйлдэл сонгох">
            <div className="ts-flex-row" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {SCAN_ACTIONS.map((a) => (
                <button
                  key={a.status}
                  className={`ts-btn ${status === a.status ? 'ts-btn-primary' : ''}`}
                  onClick={() => setStatus(a.status)}
                >
                  <i className={`bi ${a.icon}`} /> {a.label}
                </button>
              ))}
            </div>
            <input
              ref={inputRef}
              className="ts-input"
              autoFocus
              style={{ fontSize: '1.3rem', padding: '14px 16px' }}
              placeholder="Track дугаар уншуулах эсвэл бичих..."
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && scan()}
            />
            <button className="ts-btn ts-btn-primary" style={{ width: '100%', marginTop: 12, padding: 12 }} disabled={busy} onClick={scan}>
              <i className="bi bi-check2-circle" /> Бүртгэх
            </button>

            {last && (
              <div className="ts-card" style={{ marginTop: 16, padding: 14 }}>
                <div className="ts-flex-between">
                  <strong style={{ fontSize: '1.1rem' }}>{last.TrackingNo}</strong>
                  <StatusBadge label={last.StatusLabel} color={SHIPMENT_STATUS_COLOR[last.Status]} />
                </div>
                <div className="ts-page-sub">{last.CustomerName} · {last.DeclaredContent ?? ''}</div>
              </div>
            )}
          </SectionCard>
        </div>

        <div style={{ gridColumn: 'span 5' }}>
          <SectionCard icon="bi-clock-history" title={`Сүүлийн scan (${log.length})`}>
            {log.length === 0 && <div className="ts-page-sub">Хараахан scan хийгээгүй.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {log.map((l, i) => (
                <div key={i} className="ts-flex-between" style={{ fontSize: '0.85rem' }}>
                  <span><i className="bi bi-box-seam" style={{ color: 'var(--ts-brand)' }} /> {l.trackingNo}</span>
                  <span style={{ color: SHIPMENT_STATUS_COLOR[l.status] }}>{l.statusLabel}</span>
                  <span className="ts-page-sub">{dt(l.at).slice(11)}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
